import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { TYPOGRAPHY, SPACING } from '@/design-system';
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { getCountryFlag } from "@/lib/flagUtils";
import { MapPin, Users } from "lucide-react";
import { useLocalizations } from "@/hooks/useLocalizations";
import { LComponents } from "@/components/i18n";

interface LocationInfoPopoverProps {
  isoCode: string;
  name: string;
  nativeName?: string;
  locationId?: string | number;
  /** Fallback slug when locationId is not available */
  nativeSlug?: string;
  expertCount?: number;
  children: React.ReactNode;
}

export const LocationInfoPopover = ({
  isoCode,
  name,
  nativeName,
  locationId,
  nativeSlug,
  expertCount = 0,
  children,
}: LocationInfoPopoverProps) => {
  const { lComponents } = useLocalizations();
  
  // Use nativeSlug directly - no manifest dependency
  const linkPath = `/${nativeSlug ?? name.toLowerCase().replace(/\s+/g, '-')}`;

  return (
    <Popover>
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      <PopoverContent className={cn(SPACING.cardPadding, 'w-64')} align="center">
        <div className="flex flex-col items-center gap-3">
          {/* Large flag */}
          <span className="text-4xl">{getCountryFlag(isoCode)}</span>
          
          {/* Location name */}
          <div className="text-center">
            <h4 className="font-semibold text-foreground">{name}</h4>
            {nativeName && nativeName !== name && (
              <p className={cn(TYPOGRAPHY.caption)}>{nativeName}</p>
            )}
          </div>
          
          {/* Expert count */}
          <div className={cn(TYPOGRAPHY.cardDescription, 'flex items-center gap-1.5')}>
            <Users className="h-4 w-4" />
            <span>{lComponents('locations.info_popover.expert_count', { count: expertCount, defaultValue: 'Expert Count' })}</span>
          </div>
          
          {/* View Location link */}
          <Button asChild size="sm" className="w-full mt-1">
            <Link to={linkPath}>
              <MapPin className="h-4 w-4 mr-2" />
              <LComponents
                id="locations.info_popover.view_location"
                defaultValue='View Location' />
            </Link>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
